// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartHasBargainBin } from "@awc/App/Store/Selectors/CartSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

type OwnProps =
    | {
          variant: "button";
          extendedStyles?: ButtonPresentationProps;
      }
    | {
          variant: "clickable";
          extendedStyles?: ClickablePresentationProps;
      };

const mapDispatchToProps = {
    saveOrder,
};

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const { isSavingOrder } = state.pages.cart;
    const { isUpdatingCart } = state.context;
    return {
        isDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cartState.value?.lineCount === 0 ||
            cartState.value?.cartLines?.length === 0,
        canSaveOrder: cartState.value?.canSaveOrder,
        isSignedIn: cartState.value?.isAuthenticated && !cartState.value?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Change
        cartHasBargainBin: cartHasBargainBin(cartState.value),
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasToasterContext &
    ResolveThunks<typeof mapDispatchToProps>;

const CartSaveOrderButton = ({
    isDisabled,
    canSaveOrder,
    saveOrder,
    history,
    toaster,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    extendedStyles,
    isSignedIn,
    variant,
    // XNG-Change
    cartHasBargainBin,
}: Props) => {
    // XNG-Change
    if (!canSaveOrder || cartHasBargainBin) {
        return null;
    }
    const RenderComponent = variant === "button" ? Button : Clickable;
    const handleClick = () => {
        if (!isSignedIn) {
            history.push(`${signInPageUrl}?returnUrl=${cartPageUrl}`);
            return;
        }

        saveOrder({
            onSuccess: (orderId: string) => {
                history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    return (
        <RenderComponent
            disabled={isDisabled}
            data-test-selector="cart_saveOrder"
            {...extendedStyles}
            onClick={handleClick}
        >
            {translate("Save Order")}
        </RenderComponent>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(CartSaveOrderButton)));
